import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, createApiClient, type ApiClient, type MeOut } from '@grow/api-client';
import { getMsalInstance, loginRequest, ssoEnabled } from './msalConfig.js';

const TOKEN_STORAGE_KEY = 'grow.adminToken';
const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:8080';

interface AuthState {
  user: MeOut | null;
  loading: boolean;
  error: string | null;
  /** Stable API client bound to the current token + 401 handler. */
  api: ApiClient;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Whether Microsoft SSO is configured for this build. */
  ssoEnabled: boolean;
  /** True while the app is resolving an Entra redirect response on load. */
  ssoProcessing: boolean;
  /** Kick off the Entra redirect login (returns to /auth/callback). */
  loginWithMicrosoft: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

export interface AuthProviderProps {
  children: ReactNode;
  /** Test seam — when present, bypasses localStorage + API hydration. */
  initialUser?: MeOut | null;
  /** Test seam — override the API client (e.g. mock implementation). */
  apiOverride?: ApiClient;
}

export function AuthProvider({ children, initialUser, apiOverride }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(() => {
    if (initialUser !== undefined) return null;
    return typeof window === 'undefined' ? null : window.localStorage.getItem(TOKEN_STORAGE_KEY);
  });
  const [user, setUser] = useState<MeOut | null>(initialUser ?? null);
  const [loading, setLoading] = useState<boolean>(initialUser === undefined && token !== null);
  const [error, setError] = useState<string | null>(null);
  // True until the one-time redirect handler settles, so the callback screen
  // waits instead of bouncing to /login before the token exchange finishes.
  const [ssoProcessing, setSsoProcessing] = useState<boolean>(
    ssoEnabled && initialUser === undefined,
  );

  const clearToken = useCallback(() => {
    setToken(null);
    setUser(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }, []);

  const api = useMemo<ApiClient>(() => {
    if (apiOverride) return apiOverride;
    return createApiClient({
      baseUrl: API_BASE_URL,
      getAuthToken: () => token,
      onUnauthorized: () => clearToken(),
    });
  }, [token, apiOverride, clearToken]);

  // Hydrate the user on mount / token change.
  useEffect(() => {
    if (initialUser !== undefined) return; // test override path
    if (token === null) {
      setUser(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.admin
      .me()
      .then((me) => {
        if (!cancelled) {
          setUser(me);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          clearToken();
        } else {
          setError(err instanceof ApiError ? err.message : 'failed to load profile');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, api, clearToken, initialUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const res = await api.auth.login({ email, password });
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(TOKEN_STORAGE_KEY, res.access_token);
        }
        setToken(res.access_token);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'login failed');
        throw err;
      }
    },
    [api],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // Even if the server call fails, clear local state.
    }
    clearToken();
  }, [api, clearToken]);

  const loginWithMicrosoft = useCallback(async () => {
    setError(null);
    const msal = await getMsalInstance();
    // Resolve any pending/stale interaction before starting a new one — MSAL
    // throws `interaction_in_progress` otherwise.
    await msal.handleRedirectPromise();
    await msal.loginRedirect(loginRequest);
  }, []);

  // Resolve an Entra redirect response exactly once per page load. Calling
  // handleRedirectPromise on every load (not just on /auth/callback) is what
  // clears MSAL's interaction state; skipping it elsewhere is what caused
  // `interaction_in_progress`. When a redirect carries an ID token, exchange it
  // for our session token here so the callback screen only has to route onward.
  const ssoHandled = useRef(false);
  useEffect(() => {
    if (initialUser !== undefined) return; // test override path
    if (!ssoEnabled || ssoHandled.current) return;
    ssoHandled.current = true;
    // No cancellation on purpose: this is a one-time global operation, not tied
    // to the component lifecycle. Under React StrictMode the mount effect runs
    // twice — the ref guard stops the second run, and cancelling the first would
    // discard the redirect result (MSAL only yields it once), losing the login.
    (async () => {
      try {
        const msal = await getMsalInstance();
        const result = await msal.handleRedirectPromise();
        if (!result?.idToken) return;
        const res = await api.auth.ssoCallback({ id_token: result.idToken });
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(TOKEN_STORAGE_KEY, res.access_token);
        }
        setToken(res.access_token);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'single sign-on failed');
      } finally {
        setSsoProcessing(false);
      }
    })();
    // Runs once on mount; api is captured from the initial (token-less) client,
    // which is all the unauthenticated ssoCallback needs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      api,
      login,
      logout,
      ssoEnabled,
      ssoProcessing,
      loginWithMicrosoft,
    }),
    [user, loading, error, api, login, logout, ssoProcessing, loginWithMicrosoft],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
